import { CalendarViewMonth, ChevronLeft, ChevronRight, ContentCopy, ViewWeek } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import {
  FilterOptions,
  NO_PARTNER_LABEL,
  PeriodMode,
  TruckFilters,
} from 'src/pages/TruckShifts/truckShiftGrid';

type Props = {
  period: PeriodMode;
  rangeLabel: string;
  filterOptions: FilterOptions;
  filters: TruckFilters;
  onPeriodChange: (period: PeriodMode) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onFiltersChange: (filters: TruckFilters) => void;
  onOpenCopy: () => void;
  copyVisible: boolean;
  copyDisabled: boolean;
};

const NO_PARTNER_VALUE = -1;

export const ToolbarPresenter: FC<Props> = ({
  period,
  rangeLabel,
  filterOptions,
  filters,
  onPeriodChange,
  onPrev,
  onNext,
  onToday,
  onFiltersChange,
  onOpenCopy,
  copyVisible,
  copyDisabled,
}) => (
  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 1 }}>
    <IconButton size="small" onClick={onPrev} aria-label="前へ"><ChevronLeft fontSize="small" /></IconButton>
    <Button size="small" variant="outlined" onClick={onToday} sx={{ textTransform: 'none' }}>{period === 'month' ? '今月' : '今週'}</Button>
    <IconButton size="small" onClick={onNext} aria-label="次へ"><ChevronRight fontSize="small" /></IconButton>
    <Typography variant="subtitle2" fontWeight={700}>
      {rangeLabel}
    </Typography>

    <ToggleButtonGroup
      size="small"
      exclusive
      value={period}
      onChange={(_e, value: PeriodMode | null) => {
        if (value) onPeriodChange(value);
      }}
      sx={{ height: 28 }}
    >
      <ToggleButton value="week" sx={{ textTransform: 'none', fontSize: 11, px: 1.5 }}><ViewWeek fontSize="small" sx={{ mr: 0.5 }} />週</ToggleButton>
      <ToggleButton value="month" sx={{ textTransform: 'none', fontSize: 11, px: 1.5 }}><CalendarViewMonth fontSize="small" sx={{ mr: 0.5 }} />月</ToggleButton>
    </ToggleButtonGroup>

    <Select
      size="small"
      multiple
      displayEmpty
      value={filters.partnerCompanyIds.map((id) => (id === null ? NO_PARTNER_VALUE : id))}
      renderValue={(selected) => (selected.length === 0 ? '運送会社' : `運送会社(${selected.length})`)}
      onChange={(e) => {
        const raw = e.target.value as number[];
        const ids = raw.map((v) => (v === NO_PARTNER_VALUE ? null : v));
        onFiltersChange({ ...filters, partnerCompanyIds: ids });
      }}
      sx={{ minWidth: 140, height: 28, fontSize: 12, backgroundColor: '#fff' }}
    >
      {filterOptions.hasNoPartner && (
        <MenuItem value={NO_PARTNER_VALUE}>
          <Checkbox checked={filters.partnerCompanyIds.includes(null)} />
          <ListItemText primary={NO_PARTNER_LABEL} />
        </MenuItem>
      )}
      {filterOptions.companies.map((company) => (
        <MenuItem key={company.id} value={company.id}>
          <Checkbox checked={filters.partnerCompanyIds.includes(company.id)} />
          <ListItemText primary={company.name} />
        </MenuItem>
      ))}
    </Select>

    <Select
      size="small"
      multiple
      displayEmpty
      value={filters.klasses}
      renderValue={(selected) => (selected.length === 0 ? '車格' : `車格(${selected.length})`)}
      onChange={(e) => onFiltersChange({ ...filters, klasses: e.target.value as string[] })}
      sx={{ minWidth: 100, height: 28, fontSize: 12, backgroundColor: '#fff' }}
    >
      {filterOptions.klasses.map((klass) => (
        <MenuItem key={klass} value={klass}>
          <Checkbox checked={filters.klasses.includes(klass)} />
          <ListItemText primary={klass} />
        </MenuItem>
      ))}
    </Select>

    <TextField
      size="small"
      placeholder="車両名・車番・ドライバー"
      value={filters.search}
      onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
      sx={{ minWidth: 220, '& .MuiInputBase-root': { height: 28, fontSize: 12 } }}
    />
    <Box flexGrow={1} />
    {copyVisible && (
      <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={onOpenCopy} disabled={copyDisabled} sx={{ textTransform: 'none' }}>先週コピー</Button>
    )}
  </Stack>
);
